import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Search, X, ChevronRight, Folder, GitBranch, Briefcase,
  Layers, ListTree, CheckSquare, AlertTriangle, Link2, FileCheck,
  History, Loader2
} from 'lucide-react';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';

// Type icons mapping
const TYPE_ICONS = {
  portfolio: Folder,
  program: GitBranch,
  project: Briefcase,
  epic: Layers,
  feature: ListTree,
  story: CheckSquare,
  task: CheckSquare,
  issue: AlertTriangle,
  risk: AlertTriangle,
  dependency: Link2,
  decision: FileCheck,
};

// Type colors mapping
const TYPE_COLORS = {
  portfolio: '#8B5CF6',
  program: '#3B82F6',
  project: '#10B981',
  epic: '#F59E0B',
  feature: '#6366F1',
  story: '#0EA5E9',
  task: '#14B8A6',
  issue: '#EF4444',
  risk: '#F97316',
  dependency: '#8B5CF6',
  decision: '#6366F1',
};

/**
 * GlobalSearchBar - Advanced search with multi-field search, ID recognition, and suggestions
 */
const GlobalSearchBar = ({ className = '' }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [idMatch, setIdMatch] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('simpliview_recent_searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent searches');
      }
    }
  }, []);

  // Save search to recent
  const saveToRecent = useCallback((searchQuery, resultItem = null) => {
    const newRecent = [
      { query: searchQuery, item: resultItem, timestamp: Date.now() },
      ...recentSearches.filter(r => r.query !== searchQuery)
    ].slice(0, 5);
    
    setRecentSearches(newRecent);
    localStorage.setItem('simpliview_recent_searches', JSON.stringify(newRecent));
  }, [recentSearches]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim() || query.length < 2) {
      setResults([]);
      setSuggestions([]);
      setIdMatch(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        
        // Get suggestions first (faster)
        const sugRes = await api.getSearchSuggestions(query);
        setSuggestions(sugRes.data.suggestions || []);
        
        // Then full search
        const searchRes = await api.globalSearch(query);
        setResults(searchRes.data.results || []);
        setIdMatch(searchRes.data.id_match);
        
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const totalItems = suggestions.length + results.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[selectedIndex]);
          } else {
            handleResultClick(results[selectedIndex - suggestions.length]);
          }
        } else if (query.trim()) {
          handleSearch();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'id') {
      setQuery(suggestion.text);
    } else {
      navigateToItem(suggestion.type, suggestion.id);
    }
  };

  // Handle result click
  const handleResultClick = (result) => {
    saveToRecent(query, result);
    navigateToItem(result._result_type, result.id);
  };

  // Navigate to item
  const navigateToItem = async (type, id) => {
    try {
      const navRes = await api.getSearchNavigation(type, id);
      setIsOpen(false);
      setQuery('');
      navigate(navRes.data.path);
    } catch (error) {
      console.error('Navigation failed:', error);
    }
  };

  // Execute full search
  const handleSearch = () => {
    if (query.trim()) {
      saveToRecent(query);
      // Could navigate to a dedicated search results page
      // For now, just keep the dropdown open with results
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasContent = suggestions.length > 0 || results.length > 0 || recentSearches.length > 0;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search projects, epics, tasks, issues... (e.g., EPC-003)"
          className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
          data-testid="global-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!loading && query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setSuggestions([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && hasContent && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="search-dropdown">
          
          {/* ID Match Alert */}
          {idMatch && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-sm text-blue-700 flex items-center gap-2">
              <Search className="w-4 h-4" />
              Searching for {idMatch.type}: <strong>{idMatch.id_pattern}</strong>
            </div>
          )}

          {/* Suggestions Section */}
          {suggestions.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Suggestions</div>
              {suggestions.map((sug, idx) => {
                const Icon = TYPE_ICONS[sug.type] || Search;
                const color = TYPE_COLORS[sug.type] || '#6B7280';
                const isSelected = selectedIndex === idx;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(sug)}
                    className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${isSelected ? 'bg-gray-100' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{sug.text}</div>
                      {sug.key && (
                        <div className="text-xs text-gray-500">{sug.key}</div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 capitalize">{sug.type}</span>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Results Section */}
          {results.length > 0 && (
            <div className="py-2 border-t border-gray-100">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase">Results</div>
              {results.map((result, idx) => {
                const Icon = TYPE_ICONS[result._result_type] || Search;
                const color = TYPE_COLORS[result._result_type] || '#6B7280';
                const isSelected = selectedIndex === (suggestions.length + idx);
                
                return (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 ${isSelected ? 'bg-gray-100' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {result._display_name || result.name || result.title}
                      </div>
                      {result._key && (
                        <div className="text-xs text-gray-500">{result._key}</div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 capitalize">{result._result_type}</span>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Recent Searches (when no query) */}
          {!query && recentSearches.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase flex items-center gap-1">
                <History className="w-3 h-3" /> Recent Searches
              </div>
              {recentSearches.map((recent, idx) => (
                <button
                  key={idx}
                  onClick={() => setQuery(recent.query)}
                  className="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50"
                >
                  <History className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{recent.query}</span>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {query.length >= 2 && !loading && results.length === 0 && suggestions.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No results found for "{query}"
            </div>
          )}

          {/* Search Hint */}
          <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500 flex items-center justify-between">
            <span>Tip: Use IDs like "EPC-003", "T-123" for direct navigation</span>
            <span className="text-gray-400">↑↓ Navigate • Enter Select • Esc Close</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
